import React from 'react';
import { inject, observer, Provider } from 'mobx-react';
import { action, computed, observable, reaction, runInAction } from 'mobx';
import type { IReactionDisposer } from 'mobx';

import AddPaymentCard from 'src/components/account/tabs/payments/controls/add-payment-card';
import Dialog from 'click-controls/controls/dialog';
import MyAccountPaymentCardStore from 'my-account/payment-card/stores/my-account-payment-card-store';
import type PaywallDialogStore from './stores/paywall-dialog-store';
import { TOASTIFY_TYPE } from 'click-controls/constant/enums';
import { TOASTIFY_MSG } from 'click-controls/constant/message';
import ToastifyStore from 'click-controls/controls/toastify/toastify-store';
import AuthStore from 'bz-stores/lib/auth-store';
interface IProps {
  paywallDialogStore?: PaywallDialogStore;
}

@inject('paywallDialogStore')
@observer
class PaywallDialog extends React.Component<IProps> {
  private store = new MyAccountPaymentCardStore();
  disposers: IReactionDisposer[];
  @observable isSaving = false;
  @observable isLoading = false;
  @observable error: string = '';

  constructor(props: IProps) {
    super(props);

    this.disposers = [
      reaction(
        () => this.props.paywallDialogStore!.isOpen,
        (isOpen) => {
          if (isOpen) {
            this.load();
          }
        },
        { fireImmediately: true }
      ),
      reaction(
        () => AuthStore.authenticated,
        (isAuthenticated) => {
          if (!isAuthenticated) {
            this.props.paywallDialogStore!.close();
          }
        }
      ),
      reaction(
        () => this.props.paywallDialogStore!.isOpen,
        (isOpen) => {
          if (!isOpen) {
            this.store.reset();
          }
        },
        { fireImmediately: true }
      )
    ];
  }
  componentWillUnmount() {
    this.disposers.forEach((x) => x());
  }

  render() {
    const { content } = this.props.paywallDialogStore!;

    const { isValid, hasStripeLoaded } = this.store;
    const { TITLE, DESC } = content!;

    return (
      <Dialog
        title={TITLE}
        isOpen={this.isOpen}
        centered={true}
        okText={this.okText}
        onClose={this.close}
        containerClass="w-[500px]"
        disabledBtnOK={!isValid || this.isSaving}
        onOK={this.save}
        isDismissable={false}
        shouldCloseOnBlur={false}
        isLoading={!hasStripeLoaded}
      >
        <Provider myAccountPaymentCardStore={this.store}>
          <>
            <div className="mb-4 grid gap-4 text-gray-600">
              <p>{DESC}</p>
              <p className="italic">{this.rateText}</p>
              <h3 className="text-md font-semibold">Payment Card</h3>
            </div>
            <AddPaymentCard showAddButton={false} />
            {this.error && (
              <div
                className="mt-2 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700"
                role="alert"
              >
                <strong className="font-bold">Error:</strong>
                <span className="block sm:inline">{this.error}</span>
              </div>
            )}
          </>
        </Provider>
      </Dialog>
    );
  }

  @action.bound
  private async load(): Promise<void> {
    const { callback, onClose } = this.props.paywallDialogStore!;
    try {
      this.isLoading = true;
      await this.store.onLoad();

      if (!!this.store!.cardDetails?.cardExists ?? false) {
        callback?.();
        onClose();
      }
    } catch (e) {
      console.error('[Click] - PaywallDialog load error');
      console.error(e);
      throw e;
    } finally {
      this.isLoading = false;
    }
  }

  @computed
  private get rateText(): string {
    const {
      content: { RATE },
      rate
    } = this.props.paywallDialogStore!;
    if (!RATE) return '';
    return RATE.replace('{RATE}', this.store.currencySymbol + rate.toFixed(2));
  }

  @computed
  private get okText(): string {
    const { BUTTONTEXT } = this.props.paywallDialogStore!.content;

    return BUTTONTEXT || 'Add Card';
  }

  @computed
  private get isOpen(): boolean {
    const { isOpen } = this.props.paywallDialogStore!;
    const { hasCardDetails } = this.store;
    return !this.isLoading && isOpen && !hasCardDetails;
  }

  private processError(error: any): string {
    if (error) {
      if (typeof error === 'string') {
        return error;
      } else if (error?.message) {
        return error.message;
      }
    }
    return 'Payment method failed to be updated.';
  }

  @action.bound
  private close(): void {
    this.error = '';
    this.props.paywallDialogStore!.onClose();
  }

  @action.bound
  async save(): Promise<void> {
    const { callback } = this.props.paywallDialogStore!;
    const { stripeKey, saveCard, error } = this.store;
    const { confirmCardSetup } = stripeKey;
    const { setMessage } = ToastifyStore;
    try {
      this.isSaving = true;

      const clientSecret = await saveCard();
      if (!!error) {
        this.error = error;
        setMessage(TOASTIFY_TYPE.Error, TOASTIFY_MSG.PAYMENT_METHOD.ADD_EDIT_FAILED(error));
        return;
      }
      const res = await confirmCardSetup(clientSecret);

      if (res?.error) {
        this.store.paymentMethod = null;
        this.error = this.processError(res?.error);

        setMessage(
          TOASTIFY_TYPE.Error,
          TOASTIFY_MSG.PAYMENT_METHOD.SAVE_FAILED(res.error?.message)
        );
      } else {
        runInAction(() => {
          this.store.onLoad();
          this.store.isAddingCard = false;
          this.store.isLoading = false;
          callback?.();
          this.close();
          setMessage(TOASTIFY_TYPE.Success, TOASTIFY_MSG.PAYMENT_METHOD.SAVE_SUCCESS);
        });
      }
    } catch (e) {
      this.error = this.processError(e);
      setMessage(TOASTIFY_TYPE.Error, TOASTIFY_MSG.PAYMENT_METHOD.SAVE_FAILED(e));
      console.error('Issue adding card');
      console.error(e);
    } finally {
      this.isSaving = false;
    }
  }
}

export default PaywallDialog;
